import { styled } from '@mui/material/styles';
import { Box, Paper, TextField, IconButton, Typography, ListItem } from '@mui/material';

// Honey-inspired color palette
export const honeyServiceColors = {
  goldenHoney: '#F1C40F',
  brightHoney: '#F4D03F',
  honeycomb: '#D4A574',
  darkHoney: '#B8860B',
  honeyBrown: '#5D4E37',
  softCream: '#FFFEF7',
  warmAmber: '#CD853F',
  lightBrown: '#8B7355',
  honeyGradient: 'linear-gradient(135deg, #F1C40F, #F4D03F)',
  honeycombGradient: 'linear-gradient(135deg, #D4A574, #F1C40F)',
};

// Main container
export const HoneyServicesContainer = styled(Box)(({ theme }) => ({
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  color: honeyServiceColors.honeyBrown,
  maxWidth: '800px',
  margin: '0 auto',
}));

// Header section
export const HoneyServicesHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(3),
}));

// Title with honey gradient
export const HoneyServicesTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 700,
  marginBottom: theme.spacing(0.5),
  background: honeyServiceColors.honeycombGradient,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
}));

// Subtitle
export const HoneyServicesSubtitle = styled(Typography)(({ theme }) => ({
  color: honeyServiceColors.lightBrown,
  fontSize: '1.1rem',
  fontWeight: 400,
}));

// Search container
export const HoneySearchContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginBottom: theme.spacing(3),
}));

// Search input field
export const HoneySearchInput = styled(TextField)(({ theme }) => ({
  width: '100%',
  
  '& .MuiOutlinedInput-root': {
    paddingLeft: theme.spacing(1),
    borderRadius: '50px',
    border: `2px solid ${honeyServiceColors.brightHoney}`,
    background: 'white',
    transition: 'all 0.3s ease',
    
    '&:hover': {
      borderColor: honeyServiceColors.goldenHoney,
    },
    
    '&.Mui-focused': {
      borderColor: honeyServiceColors.goldenHoney,
      boxShadow: `0 0 0 3px ${honeyServiceColors.goldenHoney}20`,
    },
    
    '& fieldset': {
      border: 'none',
    },
  },
  
  '& .MuiOutlinedInput-input': {
    fontSize: '1rem',
    color: honeyServiceColors.honeyBrown,
    padding: theme.spacing(1, 1.5),
    
    '&::placeholder': {
      color: honeyServiceColors.lightBrown,
    },
  },
  
  '& .MuiInputAdornment-root': {
    color: honeyServiceColors.honeycomb,
    fontSize: '1.2rem',
  },
}));

// Dropdown container
export const HoneyDropdown = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  background: 'white',
  border: `2px solid ${honeyServiceColors.brightHoney}`,
  borderTop: 'none',
  borderRadius: '0 0 20px 20px',
  maxHeight: '300px',
  overflowY: 'auto',
  zIndex: 10,
  boxShadow: `0 8px 20px ${honeyServiceColors.honeyBrown}20`,
}));

// Dropdown item
export const HoneyDropdownItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5),
  cursor: 'pointer',
  transition: 'background 0.2s ease',
  borderBottom: `1px solid ${honeyServiceColors.brightHoney}30`,
  
  '&:last-child': {
    borderBottom: 'none',
  },
  
  '&:hover': {
    background: `linear-gradient(135deg, ${honeyServiceColors.brightHoney}10, ${honeyServiceColors.goldenHoney}10)`,
  },
}));

// Dropdown item icon
export const HoneyDropdownIcon = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(1.5),
  fontSize: '1.3rem',
}));

// Dropdown item text container
export const HoneyDropdownText = styled(Box)(({ theme }) => ({
  flex: 1,
}));

// Dropdown item name
export const HoneyDropdownName = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: honeyServiceColors.honeyBrown,
  marginBottom: theme.spacing(0.25),
}));

// Dropdown item description
export const HoneyDropdownDesc = styled(Typography)(({ theme }) => ({
  fontSize: '0.85rem',
  color: honeyServiceColors.lightBrown,
}));

// Count info
export const HoneyCountInfo = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(2),
  color: honeyServiceColors.lightBrown,
  fontWeight: 500,
}));

// Services list container
export const HoneyServicesList = styled(Paper)(({ theme }) => ({
  background: 'white',
  borderRadius: '20px',
  border: `2px solid ${honeyServiceColors.brightHoney}`,
  overflow: 'hidden',
  marginBottom: theme.spacing(3),
}));

// Individual service item
export const HoneyServiceItem = styled(ListItem)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${honeyServiceColors.brightHoney}`,
  transition: 'all 0.3s ease',
  position: 'relative',
  animation: 'slideIn 0.3s ease-out',
  
  '&:last-child': {
    borderBottom: 'none',
  },
  
  '&:hover': {
    background: `linear-gradient(135deg, ${honeyServiceColors.brightHoney}05, ${honeyServiceColors.goldenHoney}05)`,
  },
  
  '@keyframes slideIn': {
    from: {
      opacity: 0,
      transform: 'translateX(-10px)',
    },
    to: {
      opacity: 1,
      transform: 'translateX(0)',
    },
  },
}));

// Service icon circle
export const HoneyServiceIcon = styled(Box)(({ theme }) => ({
  width: '50px',
  height: '50px',
  background: honeyServiceColors.honeyGradient,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1.5rem',
  marginRight: theme.spacing(1.5),
  flexShrink: 0,
}));

// Service content area
export const HoneyServiceContent = styled(Box)(({ theme }) => ({
  flex: 1,
}));

// Service name
export const HoneyServiceName = styled(Typography)(({ theme }) => ({
  fontSize: '1.2rem',
  fontWeight: 600,
  color: honeyServiceColors.honeyBrown,
  marginBottom: theme.spacing(0.25),
}));

// Service description
export const HoneyServiceDescription = styled(Typography)(({ theme }) => ({
  color: honeyServiceColors.lightBrown,
  fontSize: '0.95rem',
  lineHeight: 1.4,
}));

// Service actions container
export const HoneyServiceActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.5),
  alignItems: 'center',
}));

// Action button
export const HoneyActionButton = styled(IconButton)(({ theme, variant = 'default' }) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'delete':
        return {
          '&:hover': {
            background: honeyServiceColors.warmAmber,
            color: 'white',
          },
        };
      default:
        return {
          background: `${honeyServiceColors.brightHoney}10`,
          color: honeyServiceColors.honeycomb,
          
          '&:hover': {
            background: honeyServiceColors.brightHoney,
            color: honeyServiceColors.honeyBrown,
          },
        };
    }
  };

  return {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    fontSize: '0.9rem',
    ...getVariantStyles(),
    
    '&:hover': {
      transform: 'scale(1.05)',
      ...getVariantStyles()['&:hover'],
    },
    
    '&.Mui-disabled': {
      opacity: 0.4,
      transform: 'none',
    },
  };
});

// Empty state container
export const HoneyEmptyState = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(4, 2),
  color: honeyServiceColors.lightBrown,
}));

// Empty state icon
export const HoneyEmptyIcon = styled(Box)(({ theme }) => ({
  fontSize: '4rem',
  marginBottom: theme.spacing(1),
  opacity: 0.5,
}));

// Expanded content area
export const HoneyExpandedContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 4),
  background: `${honeyServiceColors.brightHoney}05`,
  borderTop: `1px solid ${honeyServiceColors.brightHoney}30`,
}));

// Description input in expanded view
export const HoneyDescriptionInput = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    background: 'white',
    
    '&:hover fieldset': {
      borderColor: honeyServiceColors.honeycomb,
    },
    
    '&.Mui-focused fieldset': {
      borderColor: honeyServiceColors.goldenHoney,
    },
  },
}));

// Action buttons container in expanded view
export const HoneyExpandedActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
}));

// Service icons mapping
export const serviceIcons = {
  'wifi': '📶',
  'internet': '📶',
  'coffee': '☕',
  'tea': '🫖',
  'parking': '🅿️',
  'printing': '🖨️',
  'scanner': '📄',
  'meeting': '🏢',
  'conference': '🏢',
  'phone': '📞',
  'call': '📞',
  'booth': '📞',
  'locker': '🔒',
  'storage': '🔒',
  'kitchen': '🍽️',
  'dining': '🍽️',
  'food': '🍽️',
  'presentation': '📺',
  'projector': '📽️',
  'whiteboard': '📝',
  'board': '📝',
  'cleaning': '🧽',
  'security': '🔐',
  'access': '🚪',
  'air': '❄️',
  'conditioning': '❄️',
  'heating': '🔥',
  'reception': '🏨',
  'mail': '📮',
  'default': '⚙️',
};

// Function to get icon for service
export const getServiceIcon = (serviceName) => {
  if (!serviceName) return serviceIcons.default;
  
  const name = serviceName.toLowerCase();
  const iconKey = Object.keys(serviceIcons).find(key => 
    name.includes(key) || key.includes(name.split(' ')[0])
  );
  
  return serviceIcons[iconKey] || serviceIcons.default;
};